import {
  Profile,
  ProfileType,
  Player,
  Club,
  Coach,
  Manager,
  Staff,
} from '../../api/api-clients';
import type {
  AppProfile,
  BaseProfile,
  PlayerProfile,
  ClubProfile,
  CoachProfile,
  ManagerProfile,
  StaffProfile,
} from '../models/profile.model';
import { AppProfileType } from '../models/profile.model';

const PROFILE_TYPE_TO_APP: Record<ProfileType, AppProfileType> = {
  [ProfileType._0]: AppProfileType.None,
  [ProfileType._1]: AppProfileType.Club,
  [ProfileType._2]: AppProfileType.Manager,
  [ProfileType._3]: AppProfileType.Staff,
  [ProfileType._4]: AppProfileType.Player,
  [ProfileType._5]: AppProfileType.Coach,
};

const APP_TO_PROFILE_TYPE: Record<AppProfileType, ProfileType> = {
  [AppProfileType.None]: ProfileType._0,
  [AppProfileType.Club]: ProfileType._1,
  [AppProfileType.Manager]: ProfileType._2,
  [AppProfileType.Staff]: ProfileType._3,
  [AppProfileType.Player]: ProfileType._4,
  [AppProfileType.Coach]: ProfileType._5,
};

type ProfileDataParser = {
  fromJS: (data: unknown) => { toJSON: () => Record<string, unknown> };
};

const PROFILE_DATA_PARSERS: Record<AppProfileType, ProfileDataParser | null> = {
  [AppProfileType.None]: null,
  [AppProfileType.Club]: Club,
  [AppProfileType.Manager]: Manager,
  [AppProfileType.Staff]: Staff,
  [AppProfileType.Player]: Player,
  [AppProfileType.Coach]: Coach,
};

export class ProfileConverter {
  static toAppModel(profile: Profile): AppProfile {
    const apiType = profile.type;
    if (apiType === undefined || apiType === null) {
      throw new Error('Profile has no type');
    }

    const appType = PROFILE_TYPE_TO_APP[apiType];
    if (!appType || appType === AppProfileType.None) {
      throw new Error(`Unsupported profile type: ${apiType}`);
    }

    const base = extractBaseProfile(profile);
    const typeData = parseProfileData(profile.profileData, appType);
    const merged = { ...base, ...typeData, type: appType } as AppProfile;

    return {
      ...merged,
      nombre: extractNombre(merged),
      alias: extractAlias(merged),
    } as AppProfile;
  }

  static toApiModel(appProfile: AppProfile): Profile {
    const profileType = APP_TO_PROFILE_TYPE[appProfile.type];
    const profileData = serializeProfileData(appProfile);

    const profile = new Profile();
    profile.userId = appProfile.userId;
    profile.id = appProfile.id;
    profile.type = profileType;
    profile.profileData = profileData;
    profile.profilePicture = appProfile.profilePicture;
    profile.location = appProfile.location;
    profile.city = appProfile.city;
    profile.country = appProfile.country;
    profile.description = appProfile.description;
    profile.additionalInformation = appProfile.additionalInformation;
    profile.videos = appProfile.videos;
    profile.blocks = appProfile.blocks;
    profile.verified = appProfile.verified;
    profile.deleted = appProfile.deleted;
    profile.ratedCount = appProfile.ratedCount;
    profile.rate = appProfile.rate;
    profile.rateTactic = appProfile.rateTactic;
    profile.ratePhysic = appProfile.ratePhysic;
    profile.rateTechnique = appProfile.rateTechnique;
    profile.rateMental = appProfile.rateMental;
    profile.rateCompromise = appProfile.rateCompromise;
    profile.introVideo = appProfile.introVideo;
    profile.introVideoThumbnail = appProfile.introVideoThumbnail;
    profile.introVideoDuration = appProfile.introVideoDuration;

    return profile;
  }
}

function extractBaseProfile(profile: Profile): BaseProfile {
  return {
    nombre: '', // populated after typeData merge in toAppModel
    userId: profile.userId,
    id: profile.id,
    profilePicture: profile.profilePicture,
    location: profile.location,
    city: profile.city,
    country: profile.country,
    description: profile.description,
    additionalInformation: profile.additionalInformation,
    videos: profile.videos,
    blocks: profile.blocks,
    blocked: profile.blocked,
    verified: profile.verified,
    deleted: profile.deleted,
    registeredOn: profile.registeredOn,
    verifiedOn: profile.verifiedOn,
    verifiedUntil: profile.verifiedUntil,
    deletedOn: profile.deletedOn,
    ratedCount: profile.ratedCount,
    rate: profile.rate,
    rateTactic: profile.rateTactic,
    ratePhysic: profile.ratePhysic,
    rateTechnique: profile.rateTechnique,
    rateMental: profile.rateMental,
    rateCompromise: profile.rateCompromise,
    introVideo: profile.introVideo,
    introVideoThumbnail: profile.introVideoThumbnail,
    introVideoDuration: profile.introVideoDuration,
  };
}

function parseProfileData(
  profileData: string | undefined,
  appType: AppProfileType,
): Record<string, unknown> {
  if (!profileData) {
    return {};
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(profileData);
  } catch {
    throw new Error(`Failed to parse profileData JSON: ${profileData}`);
  }

  const parser = PROFILE_DATA_PARSERS[appType];
  if (!parser) {
    throw new Error(`No parser for profile type: ${appType}`);
  }

  // Spread the typed instance to preserve nested class instances (e.g. PersonalData with Date objects).
  // Do NOT call instance.toJSON() — that re-serializes Date → ISO string, breaking typed consumers.
  const instance = parser.fromJS(parsed);
  return { ...instance };
}

function serializeProfileData(appProfile: AppProfile): string {
  const parser = PROFILE_DATA_PARSERS[appProfile.type];
  if (!parser) {
    throw new Error(`No serializer for profile type: ${appProfile.type}`);
  }

  const typeDataKeys = getTypeDataKeys(appProfile.type);
  const typeData: Record<string, unknown> = {};
  for (const key of typeDataKeys) {
    if (key in appProfile) {
      typeData[key] = (appProfile as unknown as Record<string, unknown>)[key];
    }
  }

  // JSON round-trip normalizes Date instances to ISO strings before passing to fromJS(),
  // which prevents Date.toString() precision loss (milliseconds).
  const normalizedTypeData = JSON.parse(JSON.stringify(typeData));
  const instance = parser.fromJS(normalizedTypeData);
  return JSON.stringify(instance.toJSON());
}

function getTypeDataKeys(type: AppProfileType): string[] {
  switch (type) {
    case AppProfileType.Player:
      return [
        'personalData',
        'mainPosition',
        'otherPositions',
        'teamData',
        'teamType',
        'status',
        'lastSeasonsData',
      ] satisfies (keyof PlayerProfile)[];
    case AppProfileType.Club:
      return [
        'teamData',
        'economicConditions',
        'federationClubNumber',
      ] satisfies (keyof ClubProfile)[];
    case AppProfileType.Coach:
      return [
        'personalData',
        'training',
        'teamData',
        'teamType',
        'status',
        'lastSeasonsData',
      ] satisfies (keyof CoachProfile)[];
    case AppProfileType.Manager:
      return ['personalData', 'fifaNumber'] satisfies (keyof ManagerProfile)[];
    case AppProfileType.Staff:
      return [
        'personalData',
        'position',
        'otherStaffPositions',
        'teamData',
        'teamType',
        'status',
        'lastSeasonsData',
      ] satisfies (keyof StaffProfile)[];
    default:
      return [];
  }
}

function extractNombre(profile: AppProfile): string {
  if (profile.type === AppProfileType.Club) {
    return profile.teamData?.teamName ?? '';
  }
  const pd = profile.personalData;
  return [pd?.name, pd?.lastName].filter(Boolean).join(' ');
}

function extractAlias(profile: AppProfile): string | undefined {
  if (profile.type === AppProfileType.Club) {
    return undefined;
  }
  return profile.personalData?.alias;
}
